import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { cleanupModalBackdrops } from "../../helper/useScrollToTop";
import { DEFAULT_APP_DOWNLOAD_URL } from "../../config/appDownload";

const AddQuantityModal = ({ modelItem, cartdata, handleAddCart, handleRemoveCart }) => {
    const navigate = useNavigate();
    const [selectedVariation, setSelectedVariation] = useState({
        name: modelItem.item_name,
        price: parseFloat(modelItem.base_price),
    });

    // State for add-ons and customizations
    const [selectedAddOns, setSelectedAddOns] = useState([]);
    const [selectedDesserts, setSelectedDesserts] = useState([]);

    useEffect(() => {
        // When opening modal for a different item, reset add-ons and desserts so we don't carry over from previous item
        setSelectedAddOns([]);
        setSelectedDesserts([]);
        // If no variation array or it's empty, fallback to base item
        if (!modelItem.variation || modelItem.variation.length === 0) {
            // Calculate discounted price for base item
            const itemPrice = parseFloat(modelItem?.item_price || modelItem?.base_price || 0);
            const menuItemDiscount = parseFloat(modelItem?.menu_item_discount || 0);
            const menuItemDiscountType = modelItem?.menu_item_discount_type;
            
            let discountedPrice = itemPrice;
            if (menuItemDiscount && menuItemDiscount > 0 && menuItemDiscountType) {
                if (menuItemDiscountType.toLowerCase() === 'percentage') {
                    discountedPrice = itemPrice - (itemPrice * menuItemDiscount / 100);
                } else if (menuItemDiscountType.toLowerCase() === 'fixed') {
                    discountedPrice = itemPrice - menuItemDiscount;
                }
                if (discountedPrice < 0) discountedPrice = 0;
            }
            
            setSelectedVariation({
                name: modelItem.item_name,
                price: itemPrice,
                discount_price: discountedPrice || itemPrice,
            });
        } else {
            // Optionally, set to first variant by default
            setSelectedVariation(modelItem.variation[0]);
        }
    }, [modelItem]);

    // Function to properly close modal and clean up backdrop
    const closeModalAndCleanup = () => {
        const modal = document.getElementById('add-quantity');
        if (modal) {
            const bootstrapModal = window.bootstrap.Modal.getInstance(modal);
            if (bootstrapModal) {
                bootstrapModal.hide();
            }
        }
        
        // Use utility function to clean up modal state
        cleanupModalBackdrops();
    };

    // Ordering only on mobile app – open app download instead of cart; original navigate to cart commented below
    const navigateToCart = () => {
        closeModalAndCleanup();
        // setTimeout(() => { navigate('/cart'); }, 100);
        window.open(DEFAULT_APP_DOWNLOAD_URL, "_blank", "noopener,noreferrer");
    };

    const handleOptionChange = (variation) => {
        setSelectedVariation(variation);
        
        // If this variation has add-ons, update the available add-ons
        if (variation.add_ons) {
            let variationAddOnsData = variation.add_ons;
            if (typeof variationAddOnsData === 'string') {
                try {
                    variationAddOnsData = JSON.parse(variationAddOnsData);
                } catch (error) {
                    variationAddOnsData = [];
                }
            }
            
            // Display addons_price to user; store both for cart (grand total uses addons_price, restaurant_total uses addons_discount_price)
            const variationAddOns = Array.isArray(variationAddOnsData) ? variationAddOnsData.map(addon => {
                const addonsPrice = parseFloat(addon.addons_price || 0);
                const addonsDiscountPrice = parseFloat((addon.addons_discount_price ?? addon.addons_price) || 0);
                return {
                    name: addon.addons_name,
                    price: addonsPrice,
                    addons_price: addonsPrice,
                    addons_discount_price: addonsDiscountPrice,
                    type: addon.addons_food_type
                };
            }) : [];
            
            // Clear current add-on selections when variation changes
            setSelectedAddOns([]);
        }
    };

    // Handle add-on selection
    const handleAddOnChange = (addOn, isSelected) => {
        if (isSelected) {
            setSelectedAddOns(prev => {
                const newSelection = [...prev, addOn];
                return newSelection;
            });
        } else {
            setSelectedAddOns(prev => {
                const newSelection = prev.filter(item => item.name !== addOn.name);
                return newSelection;
            });
        }
    };

    // Handle dessert selection
    const handleDessertChange = (dessert, isSelected) => {
        if (isSelected) {
            setSelectedDesserts(prev => [...prev, dessert]);
        } else {
            setSelectedDesserts(prev => prev.filter(item => item.name !== dessert.name));
        }
    };

    // Calculate discounted price based on menu_item_discount and menu_item_discount_type
    const calculateDiscountedPrice = (item) => {
        const itemPrice = parseFloat(item?.item_price || item?.price || 0);
        const menuItemDiscount = parseFloat(item?.menu_item_discount || 0);
        const menuItemDiscountType = item?.menu_item_discount_type;

        // If no discount, return original price
        if (!menuItemDiscount || menuItemDiscount <= 0 || !menuItemDiscountType) {
            return {
                originalPrice: itemPrice,
                discountedPrice: itemPrice,
                hasDiscount: false,
                discountAmount: 0
            };
        }

        let discountedPrice = itemPrice;
        let discountAmount = 0;

        if (menuItemDiscountType.toLowerCase() === 'percentage') {
            // Percentage discount
            discountAmount = (itemPrice * menuItemDiscount) / 100;
            discountedPrice = itemPrice - discountAmount;
        } else if (menuItemDiscountType.toLowerCase() === 'fixed') {
            // Fixed discount
            discountAmount = menuItemDiscount;
            discountedPrice = itemPrice - discountAmount;
        }

        // Ensure discounted price doesn't go negative
        if (discountedPrice < 0) {
            discountedPrice = 0;
            discountAmount = itemPrice;
        }

        return {
            originalPrice: itemPrice,
            discountedPrice: discountedPrice,
            hasDiscount: true,
            discountAmount: discountAmount
        };
    };

    // Variation: original = item_price (strikethrough), discounted = item_price - variation_item_discount (fixed/%). Dynamic for any API.
    const calculateVariationDiscountedPrice = (variation) => {
        const itemPrice = parseFloat(variation?.item_price ?? variation?.price ?? 0);
        const apiPrice = parseFloat(variation?.price ?? variation?.item_price ?? 0);
        const originalPrice = itemPrice || apiPrice;
        const discount = parseFloat(variation?.variation_item_discount ?? 0);
        const discountType = (variation?.variation_item_discount_type ?? '').toLowerCase();

        if (!originalPrice) {
            return { originalPrice: 0, discountedPrice: 0, hasDiscount: false, discountAmount: 0 };
        }

        if (discount > 0 && (discountType === 'percentage' || discountType === 'fixed')) {
            let discountedPrice = originalPrice;
            if (discountType === 'percentage') {
                discountedPrice = originalPrice - (originalPrice * discount / 100);
            } else {
                discountedPrice = originalPrice - discount;
            }
            discountedPrice = Math.max(0, Math.round(discountedPrice * 100) / 100);
            return {
                originalPrice,
                discountedPrice,
                hasDiscount: true,
                discountAmount: Math.round((originalPrice - discountedPrice) * 100) / 100
            };
        }

        const discountedPrice = apiPrice > 0 && apiPrice < originalPrice ? apiPrice : originalPrice;
        return {
            originalPrice,
            discountedPrice: Math.round(discountedPrice * 100) / 100,
            hasDiscount: discountedPrice < originalPrice,
            discountAmount: Math.round((originalPrice - discountedPrice) * 100) / 100
        };
    };

    // Find the specific item+variation in the cart
    const getItemInCart = () => {
        if (!modelItem) return null;

        return cartdata.find(cartItem =>
            selectedVariation
                ? (cartItem.id === modelItem.id && cartItem.variation?.name === selectedVariation.name)
                : (cartItem.id === modelItem.id && !cartItem.variation)
        );
    };

    const cartItem = getItemInCart();
    
    // Base price: variation = discounted variation price (item_price - variation_item_discount or API price); else menu item discounted.
    const getBasePrice = () => {
        if (selectedVariation) {
            const variationInfo = calculateVariationDiscountedPrice(selectedVariation);
            return variationInfo.discountedPrice;
        }
        const itemPriceInfo = calculateDiscountedPrice(modelItem);
        return Math.round(itemPriceInfo.discountedPrice * 100) / 100;
    };

    const round2 = (n) => Math.round((n || 0) * 100) / 100;

    const getDisplayPrice = () => {
        const basePrice = round2(getBasePrice());
        const addOnsPrice = round2(selectedAddOns.reduce((sum, addOn) => sum + (addOn.price ?? 0), 0));
        const dessertsPrice = round2(selectedDesserts.reduce((sum, dessert) => sum + (dessert.price ?? 0), 0));
        const totalPrice = round2(basePrice + addOnsPrice + dessertsPrice);
        return { basePrice, addOnsPrice, dessertsPrice, totalPrice };
    };

    const handleAdd = () => {
        const restaurantIdInCart = cartdata.length > 0 ? cartdata[0]?.restaurant_id_new : null;
        const newItemRestaurantId = modelItem?.restaurant_id_new;

        if (
            restaurantIdInCart &&
            newItemRestaurantId &&
            restaurantIdInCart !== newItemRestaurantId
        ) {
            Swal.fire({
                title: "Replace cart items?",
                text: "Your cart contains items from another restaurant. Do you want to clear it and add this item?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Yes, clear it",
            }).then((result) => {
                if (result.isConfirmed) {
                    handleAddCart({
                        item: modelItem,
                        selectedVariation: selectedVariation,
                        selectedAddOns: selectedAddOns,
                        selectedDesserts: selectedDesserts,
                        clearCart: true, // Send flag to clear cart
                    });
                    Swal.fire("Updated!", "Your cart has been replaced.", "success");
                }
            });
        } else {
            handleAddCart({
                item: modelItem,
                selectedVariation: selectedVariation,
                selectedAddOns: selectedAddOns,
                selectedDesserts: selectedDesserts,
            });
        }
    };

    const handleRemove = () => {
        handleRemoveCart({
            item: modelItem,
            selectedVariation: selectedVariation
        });
    };

    if (!modelItem) return null;

    // Get real data from modelItem - no dummy data
    // Map API response to component expected format
    // Handle both regular add-ons (array) and Petpooja add-ons (JSON string)
    let addOnsData = modelItem.add_ons || [];
    if (typeof addOnsData === 'string') {
      try {
        addOnsData = JSON.parse(addOnsData);
      } catch (error) {
        addOnsData = [];
      }
    }
    
    // Get add-ons based on selected variation or base item
    const getCurrentAddOns = () => {
        if (selectedVariation && selectedVariation.add_ons) {
            let variationAddOnsData = selectedVariation.add_ons;
            if (typeof variationAddOnsData === 'string') {
                try {
                    variationAddOnsData = JSON.parse(variationAddOnsData);
                } catch (error) {
                    variationAddOnsData = [];
                }
            }
            return Array.isArray(variationAddOnsData) ? variationAddOnsData.map(addon => {
                const addonsPrice = round2(parseFloat(addon.addons_price ?? 0));
                const addonsDiscountPrice = round2(parseFloat(addon.addons_discount_price ?? addon.addons_price ?? 0));
                const effectivePrice = (addonsDiscountPrice >= 0 && addonsDiscountPrice < addonsPrice) ? addonsDiscountPrice : addonsPrice;
                return {
                    name: addon.addons_name,
                    price: effectivePrice,
                    addons_price: addonsPrice,
                    addons_discount_price: addonsDiscountPrice,
                    type: addon.addons_food_type
                };
            }) : [];
        }
        return Array.isArray(addOnsData) ? addOnsData.map(addon => {
            const addonsPrice = round2(parseFloat(addon.addons_price ?? 0));
            const addonsDiscountPrice = round2(parseFloat(addon.addons_discount_price ?? addon.addons_price ?? 0));
            const effectivePrice = (addonsDiscountPrice >= 0 && addonsDiscountPrice < addonsPrice) ? addonsDiscountPrice : addonsPrice;
            return {
                name: addon.addons_name,
                price: effectivePrice,
                addons_price: addonsPrice,
                addons_discount_price: addonsDiscountPrice,
                type: addon.addons_food_type
            };
        }) : [];
    };
    
    const addOns = getCurrentAddOns();
    const desserts = modelItem.desserts || [];
    const hasVariations = modelItem?.variation && modelItem.variation.length > 0;
    const hasAddOns = addOns.length > 0;
    const hasDesserts = desserts.length > 0;

    // Debug logging to verify data

    return (
        <div className="modal fade login-modal quantity-modal" id="add-quantity" tabIndex={-1} aria-labelledby="exampleModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content modern-food-modal">
                    <div className="modal-header modern-modal-header">
                        <div className="header-content">
                            <h1 className="modal-title">{modelItem?.item_name}</h1>
                            <div className="base-price" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                {(() => {
                                    const priceInfo = calculateDiscountedPrice(modelItem);
                                    if (priceInfo.hasDiscount) {
                                        return (
                                            <>
                                                <span style={{ 
                                                    color: '#28a745', 
                                                    fontWeight: '600',
                                                    fontSize: '18px'
                                                }}>
                                                    ₹{priceInfo.discountedPrice.toFixed(2)}
                                                </span>
                                                <span style={{
                                                    color: '#6c757d',
                                                    textDecoration: 'line-through',
                                                    fontSize: '14px',
                                                    fontWeight: '400'
                                                }}>
                                                    ₹{priceInfo.originalPrice.toFixed(2)}
                                                </span>
                                            </>
                                        );
                                    } else {
                                        return (
                                            <span style={{ fontSize: '18px', fontWeight: '600' }}>
                                                ₹{priceInfo.originalPrice.toFixed(2)}
                                            </span>
                                        );
                                    }
                                })()}
                            </div>
                        </div>
                        <button type="button" className="btn-close modern-close-btn" data-bs-dismiss="modal" aria-label="Close">
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                    
                    <div className="modal-body modern-modal-body">
                        <div className="customization-subtitle">
                            <h2>Customise as per your taste</h2>
                        </div>

                        {/* Variations Section (if exists) - display only variation options, not main item */}
                        {hasVariations && (
                            <div className="customization-section">
                                <div className="section-header">
                                    <h3>Choose Size</h3>
                                </div>
                                <div className="options-grid">
                                    {modelItem.variation.map((variant, index) => {
                                        const variantPriceInfo = calculateVariationDiscountedPrice(variant);
                                        return (
                                            <div key={index} className="option-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '10px' }}>
                                                <div className="option-content" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <div className="option-details" style={{ flex: 1 }}>
                                                        <span className="option-name" style={{ display: 'block', fontWeight: '500', marginBottom: '4px' }}>{variant.name}</span>
                                                        <div className="option-price" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                            {variantPriceInfo.hasDiscount ? (
                                                                <>
                                                                    <span style={{ color: '#28a745', fontWeight: '600' }}>
                                                                        ₹{variantPriceInfo.discountedPrice.toFixed(2)}
                                                                    </span>
                                                                    <span style={{ color: '#6c757d', textDecoration: 'line-through', fontSize: '13px' }}>
                                                                        ₹{variantPriceInfo.originalPrice.toFixed(2)}
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                <span style={{ fontWeight: '600' }}>
                                                                    ₹{variantPriceInfo.discountedPrice.toFixed(2)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <label className="modern-radio" style={{ marginLeft: '12px', flexShrink: 0 }}>
                                                    <input
                                                        type="radio"
                                                        name="variation"
                                                        checked={selectedVariation?.name === variant.name}
                                                        onChange={() => handleOptionChange(variant)}
                                                    />
                                                    <span className="radiomark"></span>
                                                </label>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Add-ons Section (only if exists) */}
                        {hasAddOns && (
                            <div className="customization-section">
                                <div className="section-header">
                                    <h3>Add-ons ({selectedAddOns.length}/{addOns.length})</h3>
                                </div>
                                <div className="options-grid">
                                    {addOns.map((addOn, index) => (
                                        <div key={index} className="option-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '10px' }}>
                                            <div className="option-content" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <div className="food-type-icon" style={{ flexShrink: 0 }}>
                                                    <i className={`fas fa-circle ${addOn.type === 'veg' ? 'veg-icon' : 'nonveg-icon'}`}></i>
                                                </div>
                                                <div className="option-details" style={{ flex: 1 }}>
                                                    <span className="option-name" style={{ display: 'block', fontWeight: '500', marginBottom: '4px' }}>{addOn.name}</span>
                                                    <div className="option-price" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                        <span style={{ color: '#28a745', fontWeight: '600' }}>+ ₹{(addOn.price || 0).toFixed(2)}</span>
                                                        {addOn.addons_discount_price != null && addOn.addons_price != null && parseFloat(addOn.addons_discount_price) < parseFloat(addOn.addons_price) && (
                                                            <span style={{ color: '#6c757d', textDecoration: 'line-through', fontSize: '13px' }}>
                                                                ₹{(parseFloat(addOn.addons_price) || 0).toFixed(2)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <label className="modern-checkbox" style={{ marginLeft: '12px', flexShrink: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedAddOns.some(item => item.name === addOn.name)}
                                                    onChange={(e) => handleAddOnChange(addOn, e.target.checked)}
                                                />
                                                <span className="checkmark"></span>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Desserts Section (only if exists) */}
                        {hasDesserts && (
                            <div className="customization-section">
                                <div className="section-header">
                                    <h3>Desserts ({selectedDesserts.length}/{desserts.length})</h3>
                                </div>
                                <div className="options-grid">
                                    {desserts.map((dessert, index) => (
                                        <div key={index} className="option-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '10px' }}>
                                            <div className="option-content" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <div className="food-type-icon" style={{ flexShrink: 0 }}>
                                                    <i className={`fas fa-circle ${dessert.type === 'veg' ? 'veg-icon' : 'nonveg-icon'}`}></i>
                                                </div>
                                                <div className="option-details" style={{ flex: 1 }}>
                                                    <span className="option-name" style={{ display: 'block', fontWeight: '500', marginBottom: '4px' }}>{dessert.name}</span>
                                                    <span className="option-price" style={{ color: '#28a745', fontWeight: '600' }}>+ ₹{dessert.price.toFixed(2)}</span>
                                                </div>
                                            </div>
                                            <label className="modern-checkbox" style={{ marginLeft: '12px', flexShrink: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedDesserts.some(item => item.name === dessert.name)}
                                                    onChange={(e) => handleDessertChange(dessert, e.target.checked)}
                                                />
                                                <span className="checkmark"></span>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Show message if no customizations available */}
                        {!hasVariations && !hasAddOns && !hasDesserts && (
                            <div className="no-customization">
                                <p>No customizations available for this item</p>
                            </div>
                        )}
                    </div>

                    <div className="modal-footer modern-modal-footer">
                        <div className="price-display" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                            {(() => {
                                const priceInfo = getDisplayPrice();
                                const hasAddOnsOrDesserts = priceInfo.addOnsPrice > 0 || priceInfo.dessertsPrice > 0;
                                
                                return (
                                    <>
                                        <span className="total-price" style={{ fontSize: '24px', fontWeight: '700', color: '#28a745' }}>
                                            ₹{priceInfo.totalPrice.toFixed(2)}
                                        </span>
                                        {hasAddOnsOrDesserts && (
                                            <div style={{ fontSize: '12px', color: '#6c757d', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                <span>Base: ₹{priceInfo.basePrice.toFixed(2)}</span>
                                                {priceInfo.addOnsPrice > 0 && <span>Add-ons: +₹{priceInfo.addOnsPrice.toFixed(2)}</span>}
                                                {priceInfo.dessertsPrice > 0 && <span>Desserts: +₹{priceInfo.dessertsPrice.toFixed(2)}</span>}
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                        {cartItem ? (
                            <div className="quantity-controls">
                                <button type="button" className="quantity-btn minus" onClick={handleRemove}>
                                    <i className="fas fa-minus" />
                                </button>
                                <input
                                    className="quantity-input"
                                    type="number"
                                    disabled
                                    value={cartItem.quantity || 0}
                                />
                                <button type="button" className="quantity-btn plus" onClick={handleAdd}>
                                    <i className="fas fa-plus" />
                                </button>
                            </div>
                        ) : (
                            <button className="add-to-cart-btn" onClick={handleAdd}>
                                Add Item to cart
                            </button>
                        )}
                        
                        {/* Add View Cart button */}
                        {cartdata.length > 0 && (
                            <button 
                                className="view-cart-btn" 
                                onClick={navigateToCart}
                                style={{
                                    background: '#e42f49',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '8px',
                                    marginLeft: '10px',
                                    cursor: 'pointer'
                                }}
                            >
                                View Cart <i className="fa-solid fa-chevron-right ms-1"></i>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddQuantityModal;

